import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CalendarDays,
  Check,
  Home,
  Loader2,
  MapPin,
  Plus,
  Trash2,
  UserRound,
  Users,
  X,
} from 'lucide-react';
import {
  convoquerRencontre,
  creerRencontre,
  fetchRencontres,
  modifierRencontre,
  supprimerRencontre,
  type EquipeComposee,
  type LicencieAligner,
  type Rencontre,
} from '../services/api';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import Aide from '../components/Aide';
import { AIDES_ADHERENTS } from '../utils/aides';

/**
 * Le calendrier du club : ce qu'on joue, et qui y va.
 *
 * CE QUE CET ÉCRAN REMPLACE
 * ---------------------------------------------------------------------------
 * Un groupe de discussion où l'entraîneur écrit « rendez-vous 13 h 30 au stade
 * de Villeneuve » le vendredi soir, et où trois parents redemandent l'adresse
 * le dimanche matin parce que le message est remonté. La convocation vit ici,
 * avec la date, le lieu et le nom de chacun, et la famille la retrouve dans son
 * espace sans avoir à faire défiler quoi que ce soit.
 *
 * L'ORDRE DE LA PAGE SUIT L'ORDRE DE LA SEMAINE
 * ---------------------------------------------------------------------------
 * Le prochain match d'abord, puis les suivants, puis ce qui est joué. Un
 * entraîneur ouvre cet écran le jeudi pour composer son groupe du dimanche, pas
 * pour relire le mois de septembre.
 */
export default function RencontresPage() {
  const { aLeDroit, isLoading } = useAuth();
  const client = useQueryClient();

  const [discipline, setDiscipline] = useState<string>('');
  const [formOuvert, setFormOuvert] = useState(false);
  const [notice, setNotice] = useState<{ type: 'success' | 'error'; texte: string } | null>(null);

  const calendrier = useQuery({
    queryKey: ['rencontres', discipline],
    queryFn: () => fetchRencontres(discipline === '' ? undefined : Number(discipline)),
  });

  const annoncer = (type: 'success' | 'error', texte: string) => {
    setNotice({ type, texte });
    if (type === 'success') {
      setTimeout(() => setNotice(null), 4000);
    }
  };

  const rafraichir = () => void client.invalidateQueries({ queryKey: ['rencontres'] });

  const creation = useMutation({
    mutationFn: (donnees: Record<string, unknown>) => creerRencontre(donnees),
    onSuccess: (d) => {
      if (d.success === false) {
        annoncer('error', d.message ?? "Cette rencontre n'a pas pu être enregistrée.");
        return;
      }
      annoncer('success', d.message ?? 'Rencontre enregistrée.');
      setFormOuvert(false);
      rafraichir();
    },
    onError: (e: Error) => annoncer('error', e.message),
  });

  if (isLoading) {
    return null;
  }

  if (!aLeDroit('voir_adherents')) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center">
        <h1 className="text-xl font-semibold text-gray-900 mb-2">Calendrier</h1>
        <p className="text-sm text-gray-500">
          Le calendrier appartient au bureau et aux entraîneurs. Demandez-leur d'ouvrir ce droit si
          vous en avez l'usage.
        </p>
      </div>
    );
  }

  const peutGerer = aLeDroit('gerer_adherents');
  const rencontres = calendrier.data?.rencontres ?? [];
  const disciplines = calendrier.data?.disciplines ?? [];
  const bilan = calendrier.data?.bilan ?? null;

  const equipes = calendrier.data?.equipes ?? [];
  const licencies = calendrier.data?.licencies ?? [];

  const aVenir = rencontres.filter((r) => r.aVenir);
  const passees = [...rencontres.filter((r) => !r.aVenir)].reverse();

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <CalendarDays className="w-6 h-6 text-primary-600" />
          Calendrier
          {AIDES_ADHERENTS.Calendrier !== undefined && (
            <Aide titre="Calendrier">{AIDES_ADHERENTS.Calendrier}</Aide>
          )}
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Les rencontres de la saison {calendrier.data?.saison?.libelle ?? ''}, et le groupe convoqué
          pour chacune. Vos licenciés le retrouvent dans leur espace.
        </p>
      </header>

      {notice !== null && (
        <p
          className={`mb-4 rounded-lg px-3 py-2 text-sm ${
            notice.type === 'success'
              ? 'bg-green-50 border border-green-200 text-green-800'
              : 'bg-red-50 border border-red-200 text-red-800'
          }`}
        >
          {notice.texte}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-3 mb-5">
        {/*
          Le filtre par discipline n'apparaît qu'à partir de deux : un club
          mono-discipline n'a rien à filtrer, et un menu à une entrée demande
          un geste pour une décision qui n'existe pas.
        */}
        {disciplines.length > 1 && (
          <select
            value={discipline}
            onChange={(e) => setDiscipline(e.target.value)}
            className="px-3 py-2 rounded-lg border border-gray-300 text-sm"
          >
            <option value="">Toutes les disciplines</option>
            {disciplines.map((d) => (
              <option key={d.id} value={String(d.id)}>
                {d.nom}
              </option>
            ))}
          </select>
        )}

        {bilan !== null && bilan.jouees > 0 && (
          <p className="text-sm text-gray-600">
            <strong>{bilan.jouees}</strong> jouée{bilan.jouees > 1 ? 's' : ''} — {bilan.gagnees}{' '}
            gagnée{bilan.gagnees > 1 ? 's' : ''}, {bilan.nulles} nulle{bilan.nulles > 1 ? 's' : ''},{' '}
            {bilan.perdues} perdue{bilan.perdues > 1 ? 's' : ''}
          </p>
        )}

        {peutGerer && !formOuvert && (
          <button
            onClick={() => setFormOuvert(true)}
            className="ml-auto inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 min-h-[44px]"
          >
            <Plus className="w-4 h-4" />
            Ajouter une rencontre
          </button>
        )}
      </div>

      {formOuvert && (
        <FormulaireRencontre
          disciplines={disciplines}
          enCours={creation.isPending}
          onAnnuler={() => setFormOuvert(false)}
          onValider={(donnees) => creation.mutate(donnees)}
        />
      )}

      {calendrier.isPending ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
        </div>
      ) : rencontres.length === 0 ? (
        <p className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-8 text-center text-sm text-gray-500">
          Aucune rencontre au calendrier. Ajoutez-en une : vos licenciés la verront dans leur espace,
          avec la date, le lieu et leur place dans le groupe.
        </p>
      ) : (
        <div className="space-y-6">
          {aVenir.length > 0 && (
            <section>
              <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                À venir
              </h2>
              <ul className="space-y-3">
                {aVenir.map((r) => (
                  <CarteRencontre
                    key={r.id}
                    rencontre={r}
                    peutGerer={peutGerer}
                    equipes={equipes}
                    licencies={licencies}
                    onNotice={annoncer}
                    onChangement={rafraichir}
                  />
                ))}
              </ul>
            </section>
          )}

          {passees.length > 0 && (
            <section>
              <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                Déjà jouées
              </h2>
              <ul className="space-y-3">
                {passees.map((r) => (
                  <CarteRencontre
                    key={r.id}
                    rencontre={r}
                    peutGerer={peutGerer}
                    equipes={equipes}
                    licencies={licencies}
                    onNotice={annoncer}
                    onChangement={rafraichir}
                  />
                ))}
              </ul>
            </section>
          )}
        </div>
      )}
    </div>
  );
}

const STATUT_LIBELLE: Record<string, string> = {
  a_venir: 'à venir',
  jouee: 'jouée',
  annulee: 'annulée',
  reportee: 'reportée',
};

const ISSUE_CLASSE: Record<string, string> = {
  gagnee: 'text-green-700',
  nulle: 'text-gray-600',
  perdue: 'text-red-700',
};

function jourEtHeure(iso: string | null): string {
  if (iso === null) return '';
  return new Date(iso).toLocaleString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
  });
}


/**
 * Une rencontre, avec son résultat et son groupe.
 *
 * Le groupe reste replié : un entraîneur ouvre le calendrier pour vérifier une
 * date bien plus souvent que pour composer, et déplier trente noms sur chaque
 * carte rendrait la page illisible dès le mois d'octobre.
 */
function CarteRencontre({
  rencontre,
  peutGerer,
  equipes,
  licencies,
  onNotice,
  onChangement,
}: {
  rencontre: Rencontre;
  peutGerer: boolean;
  equipes: EquipeComposee[];
  licencies: LicencieAligner[];
  onNotice: (type: 'success' | 'error', texte: string) => void;
  onChangement: () => void;
}) {
  const [groupeOuvert, setGroupeOuvert] = useState(false);
  const [confirme, setConfirme] = useState(false);
  const [scoreNous, setScoreNous] = useState(
    rencontre.scoreNous === null ? '' : String(rencontre.scoreNous),
  );
  const [scoreEux, setScoreEux] = useState(
    rencontre.scoreEux === null ? '' : String(rencontre.scoreEux),
  );

  const modification = useMutation({
    mutationFn: (donnees: Record<string, unknown>) => modifierRencontre(rencontre.id, donnees),
    onSuccess: (d) => {
      if (d.success === false) {
        onNotice('error', d.message ?? 'Enregistrement refusé.');
        return;
      }
      onNotice('success', d.message ?? 'Rencontre enregistrée.');
      onChangement();
    },
    onError: (e: Error) => onNotice('error', e.message),
  });

  const suppression = useMutation({
    mutationFn: () => supprimerRencontre(rencontre.id),
    onSuccess: (d) => {
      onNotice('success', d.message ?? 'Rencontre retirée.');
      onChangement();
    },
    onError: (e: Error) => onNotice('error', e.message),
  });

  const retenus = (rencontre.groupe ?? []).length;

  return (
    <li className="rounded-xl border border-gray-200 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-semibold text-gray-900">
            {rencontre.aDomicile ? 'Contre' : 'Chez'} {rencontre.adversaire}
          </p>
          <p className="text-sm text-gray-600 first-letter:uppercase">
            {jourEtHeure(rencontre.debut)}
          </p>
          <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
            <span className="inline-flex items-center gap-1">
              {rencontre.aDomicile ? (
                <>
                  <Home className="w-3.5 h-3.5" />à domicile
                </>
              ) : (
                <>
                  <MapPin className="w-3.5 h-3.5" />
                  déplacement
                </>
              )}
            </span>
            {rencontre.lieu && <span>{rencontre.lieu}</span>}
            {rencontre.competition && <span>{rencontre.competition}</span>}
            {rencontre.equipe && <span>{rencontre.equipe}</span>}
            {rencontre.discipline && <span>{rencontre.discipline}</span>}
            {rencontre.statut !== 'a_venir' && rencontre.statut !== 'jouee' && (
              <span className="text-amber-700">{STATUT_LIBELLE[rencontre.statut]}</span>
            )}
          </p>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {rencontre.score !== null && (
            <span className="tabular-nums font-semibold text-gray-900">{rencontre.score}</span>
          )}
          {rencontre.issue !== null && (
            <span className={`text-xs ${ISSUE_CLASSE[rencontre.issue] ?? 'text-gray-500'}`}>
              {rencontre.issue === 'gagnee'
                ? 'gagné'
                : rencontre.issue === 'nulle'
                  ? 'nul'
                  : 'perdu'}
            </span>
          )}
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-3 border-t border-gray-100 pt-3">
        <button
          onClick={() => setGroupeOuvert((o) => !o)}
          className="inline-flex items-center gap-1.5 text-sm text-gray-700 hover:text-gray-900 min-h-[44px]"
        >
          <Users className="w-4 h-4" />
          {retenus === 0 ? 'Composer le groupe' : `Groupe (${retenus})`}
        </button>

        {peutGerer && (
          <>
            {/*
              LE SCORE SE SAISIT ICI, ET IL POSE LE STATUT.

              Saisir un résultat sur une rencontre encore « à venir » donnerait
              une saison qui affiche des victoires avant d'avoir été jouée. Le
              serveur bascule donc le statut en même temps — c'est ce que
              voulait dire celui qui saisit un score, et le lui demander en
              deux gestes ferait oublier le second.
            */}
            <span className="flex items-center gap-1.5 text-sm text-gray-600">
              <input
                value={scoreNous}
                onChange={(e) => setScoreNous(e.target.value)}
                inputMode="numeric"
                placeholder="—"
                aria-label="Notre score"
                className="w-12 px-2 py-1.5 rounded-lg border border-gray-300 text-center text-sm tabular-nums"
              />
              <span>–</span>
              <input
                value={scoreEux}
                onChange={(e) => setScoreEux(e.target.value)}
                inputMode="numeric"
                placeholder="—"
                aria-label="Leur score"
                className="w-12 px-2 py-1.5 rounded-lg border border-gray-300 text-center text-sm tabular-nums"
              />
              <button
                onClick={() =>
                  modification.mutate({
                    scoreNous: scoreNous === '' ? null : scoreNous,
                    scoreEux: scoreEux === '' ? null : scoreEux,
                  })
                }
                disabled={modification.isPending}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-gray-300 text-sm hover:bg-gray-50 disabled:opacity-60"
              >
                {modification.isPending ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Check className="w-3.5 h-3.5" />
                )}
                Résultat
              </button>
            </span>

            <select
              value={rencontre.statut}
              onChange={(e) => modification.mutate({ statut: e.target.value })}
              className="px-2.5 py-1.5 rounded-lg border border-gray-300 text-sm"
            >
              {Object.entries(STATUT_LIBELLE).map(([code, label]) => (
                <option key={code} value={code}>
                  {label}
                </option>
              ))}
            </select>

            {/*
              La suppression demande un second clic sur le même bouton.

              Rien de financier n'y pend, et le geste vise une saisie ratée —
              mauvaise date, doublon. Un match qui n'a pas eu lieu se marque
              « annulé » : c'est un statut, et il reste visible.
            */}
            <button
              onClick={() => {
                if (confirme) {
                  suppression.mutate();
                } else {
                  setConfirme(true);
                }
              }}
              onBlur={() => setConfirme(false)}
              disabled={suppression.isPending}
              className={`ml-auto inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm min-h-[44px] ${
                confirme
                  ? 'border border-red-300 bg-red-50 text-red-800'
                  : 'text-gray-500 hover:bg-gray-50'
              } disabled:opacity-60`}
            >
              {suppression.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4" />
              )}
              {confirme ? 'Confirmer' : 'Retirer'}
            </button>
          </>
        )}
      </div>

      {groupeOuvert && (
        <Groupe
          rencontre={rencontre}
          peutGerer={peutGerer}
          equipes={equipes}
          licencies={licencies}
          onNotice={onNotice}
          onChangement={onChangement}
        />
      )}
    </li>
  );
}

const PLACES: { code: string; label: string }[] = [
  { code: 'convoque', label: 'convoqué' },
  { code: 'present', label: 'présent' },
  { code: 'absent', label: 'absent' },
  { code: 'excuse', label: 'excusé' },
];

/**
 * Qui est attendu, et ce qu'il en est advenu.
 *
 * POURQUOI LA LISTE PART ENTIÈRE
 * ---------------------------------------------------------------------------
 * L'entraîneur coche son groupe d'un coup le jeudi soir, et le corrige le
 * lundi. Une liste envoyée entière ne laisse pas de convoqué fantôme derrière
 * elle, alors qu'un ajout-retrait ligne par ligne finit toujours par en
 * oublier un — celui qu'on avait décoché puis recoché.
 *
 * Le serveur garde le statut et la note de ceux qui restent : ajouter un
 * remplaçant ne remet pas les autres à « convoqué ».
 */
/**
 * Qui est attendu, et ce qu'il en est advenu.
 *
 * ON PART DE L'ÉQUIPE, PAS D'UNE LISTE VIDE
 * ---------------------------------------------------------------------------
 * L'entraîneur a composé ses U15 en septembre. Convoquer pour le match de
 * dimanche partait pourtant d'une liste vide, où il fallait recocher les
 * quatorze mêmes noms parmi cent cinquante — chaque semaine.
 *
 * « Prendre l'équipe U15 » coche les quatorze d'un clic ; on retire ensuite les
 * deux absents. Quand la rencontre porte déjà une équipe, c'est celle-là qui
 * est proposée en premier.
 *
 * ET DES LICENCIÉS, PAS DES ADHÉRENTS
 * ---------------------------------------------------------------------------
 * On cochait dans le registre entier. Rien n'empêchait donc d'inscrire sur une
 * feuille de match quelqu'un qui n'a pas de licence cette saison — c'est-à-dire
 * quelqu'un qui n'est pas assuré, ce qui ne se découvre qu'après la blessure.
 *
 * La liste ne montre que les licenciés, filtrés sur la discipline de la
 * rencontre quand elle en porte une.
 *
 * POURQUOI LA LISTE PART ENTIÈRE À L'ENREGISTREMENT
 * ---------------------------------------------------------------------------
 * L'entraîneur coche d'un coup le jeudi soir et corrige le lundi. Une liste
 * envoyée entière ne laisse pas de convoqué fantôme derrière elle, alors qu'un
 * ajout-retrait ligne par ligne finit toujours par en oublier un — celui qu'on
 * avait décoché puis recoché.
 *
 * Le serveur garde le statut et la note de ceux qui restent : ajouter un
 * remplaçant ne remet pas les autres à « convoqué ».
 */
function Groupe({
  rencontre,
  peutGerer,
  equipes,
  licencies,
  onNotice,
  onChangement,
}: {
  rencontre: Rencontre;
  peutGerer: boolean;
  equipes: EquipeComposee[];
  licencies: LicencieAligner[];
  onNotice: (type: 'success' | 'error', texte: string) => void;
  onChangement: () => void;
}) {
  const [places, setPlaces] = useState<Record<number, string>>(() => {
    const depart: Record<number, string> = {};
    for (const p of rencontre.groupe ?? []) {
      if (p.adherent !== null) {
        depart[p.adherent] = p.statut;
      }
    }
    return depart;
  });

  const envoi = useMutation({
    mutationFn: () =>
      convoquerRencontre(
        rencontre.id,
        Object.entries(places).map(([adherent, statut]) => ({
          adherent: Number(adherent),
          statut,
        })),
      ),
    onSuccess: (d) => {
      onNotice('success', d.message ?? 'Groupe enregistré.');
      onChangement();
    },
    onError: (e: Error) => onNotice('error', e.message),
  });

  /*
    Les licenciés de LA discipline de cette rencontre.

    Sans discipline — une rencontre d'un club mono-discipline, ou saisie avant
    le module — on les prend tous : filtrer sur rien ne montrerait personne.

    Le même adhérent apparaît une fois par licence : on le dédoublonne ici,
    puisque la convocation porte sur la personne et non sur sa licence.
  */
  const proposables = useMemo(() => {
    const vus = new Set<number>();
    const retenus: LicencieAligner[] = [];

    for (const l of licencies) {
      if (
        rencontre.disciplineId !== null &&
        l.disciplineId !== null &&
        l.disciplineId !== rencontre.disciplineId
      ) {
        continue;
      }

      if (!vus.has(l.adherentId)) {
        vus.add(l.adherentId);
        retenus.push(l);
      }
    }

    return retenus;
  }, [licencies, rencontre.disciplineId]);

  /*
    Les équipes qu'on peut prendre d'un clic.

    Celle de la rencontre en tête si elle en porte une, puis celles de la même
    discipline. Une équipe de gymnastique n'a rien à proposer pour un match de
    football, et l'afficher ferait chercher la bonne dans une liste de six.
  */
  const proposees = useMemo(() => {
    const utiles = equipes.filter(
      (e) =>
        e.joueurs.length > 0 &&
        (rencontre.disciplineId === null ||
          e.disciplineId === null ||
          e.disciplineId === rencontre.disciplineId),
    );

    return [...utiles].sort((a, b) => {
      if (a.id === rencontre.equipeId) return -1;
      if (b.id === rencontre.equipeId) return 1;
      return a.nom.localeCompare(b.nom, 'fr');
    });
  }, [equipes, rencontre.disciplineId, rencontre.equipeId]);

  const prendre = (equipe: EquipeComposee) =>
    setPlaces((actuelles) => {
      const suite = { ...actuelles };
      for (const id of equipe.joueurs) {
        // Ceux qui y sont déjà gardent leur statut : reprendre l'équipe après
        // avoir pointé les présences ne doit pas tout remettre à « convoqué ».
        suite[id] ??= 'convoque';
      }
      return suite;
    });

  const retenus = Object.keys(places).length;

  return (
    <div className="mt-3 border-t border-gray-100 pt-3">
      {peutGerer && proposees.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <span className="text-xs text-gray-500">Prendre une équipe :</span>
          {proposees.map((e) => (
            <button
              key={e.id}
              type="button"
              onClick={() => prendre(e)}
              className="inline-flex items-center gap-1.5 rounded-full border border-primary-200 bg-primary-50 px-3 py-1 text-xs font-medium text-primary-800 hover:bg-primary-100"
              title={`Convoquer les ${e.joueurs.length} joueurs de ${e.nom}`}
            >
              <Users className="w-3 h-3" />
              {e.nom}
              <span className="text-primary-600">({e.joueurs.length})</span>
            </button>
          ))}
          {retenus > 0 && (
            <button
              type="button"
              onClick={() => setPlaces({})}
              className="text-xs text-gray-500 hover:text-gray-800"
            >
              Vider
            </button>
          )}
        </div>
      )}

      {proposables.length === 0 ? (
        <p className="text-sm text-gray-500">
          Aucun licencié
          {rencontre.discipline !== null ? ` en ${rencontre.discipline}` : ''} pour cette saison.
          Inscrivez-les depuis le registre : on ne convoque que des gens assurés.
        </p>
      ) : (
        <ul className="max-h-72 overflow-y-auto divide-y divide-gray-100">
          {proposables.map((a) => {
            const place = places[a.adherentId];

            return (
              <li key={a.adherentId} className="flex flex-wrap items-center gap-2 py-1.5 text-sm">
                <label className="flex flex-1 min-w-0 items-center gap-2">
                  <input
                    type="checkbox"
                    checked={place !== undefined}
                    disabled={!peutGerer}
                    onChange={(e) =>
                      setPlaces((actuelles) => {
                        const suite = { ...actuelles };
                        if (e.target.checked) {
                          suite[a.adherentId] = 'convoque';
                        } else {
                          delete suite[a.adherentId];
                        }
                        return suite;
                      })
                    }
                    className="rounded border-gray-300 text-primary-600"
                  />
                  <span className="truncate text-gray-800">
                    {a.nomComplet}
                    {a.categorie && <span className="text-gray-500"> · {a.categorie}</span>}
                  </span>
                </label>

                {/*
                  La fiche d'un convoqué, en un clic.

                  Un entraîneur qui compose se demande si untel a réglé sa
                  cotisation, ou si son certificat est à jour. Il devait
                  retenir le nom, sortir du calendrier, ouvrir le répertoire,
                  chercher.
                */}
                <Link
                  to={`/adherents/${a.adherentId}`}
                  title={`Ouvrir la fiche de ${a.nomComplet}`}
                  aria-label={`Ouvrir la fiche de ${a.nomComplet}`}
                  className="shrink-0 p-1 rounded text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                >
                  <UserRound className="w-3.5 h-3.5" />
                </Link>

                {place !== undefined && (
                  <select
                    value={place}
                    disabled={!peutGerer}
                    onChange={(e) =>
                      setPlaces((actuelles) => ({ ...actuelles, [a.adherentId]: e.target.value }))
                    }
                    className="px-2 py-1 rounded-lg border border-gray-300 text-xs"
                  >
                    {PLACES.map((p) => (
                      <option key={p.code} value={p.code}>
                        {p.label}
                      </option>
                    ))}
                  </select>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {peutGerer && (
        <div className="mt-3 flex items-center gap-3">
          <button
            onClick={() => envoi.mutate()}
            disabled={envoi.isPending}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-60 min-h-[44px]"
          >
            {envoi.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            Enregistrer le groupe
          </button>
          <span className="text-xs text-gray-500">
            {retenus} retenu{retenus > 1 ? 's' : ''}
          </span>
        </div>
      )}
    </div>
  );
}

function FormulaireRencontre({
  disciplines,
  enCours,
  onAnnuler,
  onValider,
}: {
  disciplines: { id: number; nom: string }[];
  enCours: boolean;
  onAnnuler: () => void;
  onValider: (donnees: Record<string, unknown>) => void;
}) {
  const [form, setForm] = useState({
    debut: '',
    adversaire: '',
    aDomicile: true,
    lieu: '',
    competition: '',
    discipline: disciplines.length === 1 ? String(disciplines[0].id) : '',
  });

  return (
    <div className="mb-5 rounded-xl border border-gray-200 p-4 space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="text-xs text-gray-500">
          Date et heure
          <input
            type="datetime-local"
            value={form.debut}
            onChange={(e) => setForm({ ...form, debut: e.target.value })}
            className="block w-full mt-1 px-3 py-2 rounded-lg border border-gray-300 text-sm text-gray-800"
          />
        </label>

        <label className="text-xs text-gray-500">
          Adversaire
          <input
            value={form.adversaire}
            onChange={(e) => setForm({ ...form, adversaire: e.target.value })}
            placeholder="AS Voisine"
            className="block w-full mt-1 px-3 py-2 rounded-lg border border-gray-300 text-sm"
          />
        </label>

        <label className="text-xs text-gray-500">
          Lieu
          <input
            value={form.lieu}
            onChange={(e) => setForm({ ...form, lieu: e.target.value })}
            placeholder="Stade municipal"
            className="block w-full mt-1 px-3 py-2 rounded-lg border border-gray-300 text-sm"
          />
        </label>

        <label className="text-xs text-gray-500">
          Compétition
          <input
            value={form.competition}
            onChange={(e) => setForm({ ...form, competition: e.target.value })}
            placeholder="Championnat, coupe, tournoi…"
            className="block w-full mt-1 px-3 py-2 rounded-lg border border-gray-300 text-sm"
          />
        </label>

        {disciplines.length > 1 && (
          <label className="text-xs text-gray-500 sm:col-span-2">
            Discipline
            <select
              value={form.discipline}
              onChange={(e) => setForm({ ...form, discipline: e.target.value })}
              className="block w-full mt-1 px-3 py-2 rounded-lg border border-gray-300 text-sm"
            >
              <option value="">Sans discipline</option>
              {disciplines.map((d) => (
                <option key={d.id} value={String(d.id)}>
                  {d.nom}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      <label className="flex items-center gap-2 text-sm text-gray-700">
        <input
          type="checkbox"
          checked={form.aDomicile}
          onChange={(e) => setForm({ ...form, aDomicile: e.target.checked })}
          className="rounded border-gray-300 text-primary-600"
        />
        À domicile
        <span className="text-xs text-gray-500">
          — c'est la première chose qu'une famille regarde : elle décide s'il faut prendre la voiture.
        </span>
      </label>

      <div className="flex justify-end gap-3">
        <button
          onClick={onAnnuler}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 min-h-[44px]"
        >
          <X className="w-4 h-4" />
          Annuler
        </button>
        <button
          onClick={() =>
            onValider({
              debut: form.debut,
              adversaire: form.adversaire,
              aDomicile: form.aDomicile,
              lieu: form.lieu,
              competition: form.competition,
              discipline: form.discipline === '' ? null : Number(form.discipline),
            })
          }
          disabled={enCours}
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-60 min-h-[44px]"
        >
          {enCours && <Loader2 className="w-4 h-4 animate-spin" />}
          Ajouter au calendrier
        </button>
      </div>
    </div>
  );
}

